import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ListingCard } from '@/components/ListingCard';
import { CUISINES } from '@/data/cuisines';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['Hot Meals', 'Baked Goods', 'Desserts', 'Sauces & Preserves', 'Meal Prep'];
const dietaryOptions = ['Vegan', 'Vegetarian', 'Gluten-Free', 'Halal', 'Nut-Free', 'Dairy-Free'];

export const ListingsPage = () => {
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    dietary: '',
    cuisine: searchParams.get('cuisine') || '',
    location: searchParams.get('location') || '',
    search: searchParams.get('search') || ''
  });

  useEffect(() => {
    fetchListings();
  }, [filters]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.dietary) params.append('dietary', filters.dietary);
      if (filters.cuisine) params.append('cuisine', filters.cuisine);
      if (filters.location) params.append('location', filters.location);
      
      const { data } = await axios.get(`${API}/listings?${params.toString()}`);
      
      let filteredData = data;
      if (filters.search) {
        filteredData = data.filter(listing => 
          listing.title.toLowerCase().includes(filters.search.toLowerCase()) ||
          listing.description.toLowerCase().includes(filters.search.toLowerCase())
        );
      }
      
      setListings(filteredData);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 
          className="text-4xl sm:text-5xl mb-8 tracking-tight" 
          style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
          data-testid="listings-heading"
        >
          Discover Local Food
        </h1>

        <div className="mb-8 p-6 rounded-md border border-[#E5E0D8]" style={{ backgroundColor: '#FFFFFF' }} data-testid="filters-section">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={20} strokeWidth={1.5} />
            <h2 style={{ fontFamily: 'Manrope', fontWeight: 600, fontSize: '18px' }}>Filters</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
                className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                data-testid="category-filter"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                Cuisine
              </label>
              <select
                value={filters.cuisine}
                onChange={(e) => setFilters({...filters, cuisine: e.target.value})}
                className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                data-testid="cuisine-filter"
              >
                <option value="">All Cuisines</option>
                {CUISINES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                Dietary
              </label>
              <select
                value={filters.dietary}
                onChange={(e) => setFilters({...filters, dietary: e.target.value})}
                className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                data-testid="dietary-filter"
              >
                <option value="">All Diets</option>
                {dietaryOptions.map((diet) => (
                  <option key={diet} value={diet}>{diet}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                Location
              </label>
              <input
                type="text"
                value={filters.location}
                onChange={(e) => setFilters({...filters, location: e.target.value})}
                placeholder="Enter city or area"
                className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                data-testid="location-filter-input"
              />
            </div>
          </div>
          <Button 
            onClick={() => setFilters({ category: '', dietary: '', cuisine: '', location: '', search: '' })}
            variant="ghost"
            className="mt-4"
            style={{ fontFamily: 'Manrope' }}
            data-testid="clear-filters-btn"
          >
            Clear Filters
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading-state">
            <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>Loading listings...</p>
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="listings-grid">
            {listings.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12" data-testid="no-results">
            <p style={{ fontFamily: 'Manrope', fontSize: '18px', color: '#5C5C5C' }}>
              No listings found. Try adjusting your filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};